import React, { useState } from 'react';
import { Plus, Shield, Building2, Users as UsersIcon, Trash2, Ban, CheckCircle, Edit2, X } from 'lucide-react';
import { useWorkspace } from '../contexts/WorkspaceContext';
import { CreateUserModal } from '../components/CreateUserModal';
import { CreateWorkspaceModal } from '../components/CreateWorkspaceModal';
export function SuperAdminSettings() {
  const {
    allUsers,
    roles,
    workspaces,
    currentUser,
    updateUserStatus,
    updateUserRole,
    deleteUser
  } = useWorkspace();
  const [activeTab, setActiveTab] = useState<'users' | 'workspaces'>('users');
  const [showUserModal, setShowUserModal] = useState(false);
  const [showWorkspaceModal, setShowWorkspaceModal] = useState(false);
  const [selectedWorkspaceFilter, setSelectedWorkspaceFilter] = useState<string>('all');
  const [editingUserId, setEditingUserId] = useState<number | null>(null);
  const [selectedRoleId, setSelectedRoleId] = useState<number>(0);
  // Security check
  if (currentUser.role !== 'Super Admin') {
    return <div className="p-8 text-center text-red-600" data-id="element-1909">
        Access Denied: Super Admin privileges required.
      </div>;
  }
  const filteredUsers = selectedWorkspaceFilter === 'all' ? allUsers : allUsers.filter(u => u.workspaceId === selectedWorkspaceFilter);
  const handleStartEditRole = (userId: number, currentRoleId: number) => {
    setEditingUserId(userId);
    setSelectedRoleId(currentRoleId);
  };
  const handleSaveRole = (userId: number) => {
    if (selectedRoleId) {
      updateUserRole(userId, selectedRoleId);
    }
    setEditingUserId(null);
  };
  const getRoleBadgeColor = (roleName: string) => {
    switch (roleName) {
      case 'Super Admin':
        return 'bg-purple-100 text-purple-800';
      case 'Workspace Admin':
        return 'bg-blue-100 text-blue-800';
      case 'Subregion Admin':
        return 'bg-indigo-100 text-indigo-800';
      case 'Operator':
        return 'bg-green-100 text-green-800';
      case 'Viewer':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };
  return <div className="space-y-6" data-id="element-1910">
      <div className="flex items-center justify-between" data-id="element-1911">
        <div data-id="element-1912">
          <h1 className="text-2xl font-semibold text-gray-900" data-id="element-1913">
            Super Admin Settings
          </h1>
          <p className="mt-1 text-sm text-gray-500" data-id="element-1914">
            Global management of users and workspaces across all teams.
          </p>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-4 gap-4" data-id="element-1915">
        <div className="bg-white border border-gray-200 rounded-lg p-4" data-id="element-1916">
          <div className="flex items-center gap-3" data-id="element-1917">
            <div className="p-2 bg-blue-50 rounded-lg" data-id="element-1918">
              <UsersIcon className="h-5 w-5 text-blue-600" data-id="element-1919" />
            </div>
            <div data-id="element-1920">
              <p className="text-2xl font-semibold text-gray-900" data-id="element-1921">
                {allUsers.length}
              </p>
              <p className="text-xs text-gray-500" data-id="element-1922">Total Users</p>
            </div>
          </div>
        </div>
        <div className="bg-white border border-gray-200 rounded-lg p-4" data-id="element-1923">
          <div className="flex items-center gap-3" data-id="element-1924">
            <div className="p-2 bg-green-50 rounded-lg" data-id="element-1925">
              <Building2 className="h-5 w-5 text-green-600" data-id="element-1926" />
            </div>
            <div data-id="element-1927">
              <p className="text-2xl font-semibold text-gray-900" data-id="element-1928">
                {workspaces.length}
              </p>
              <p className="text-xs text-gray-500" data-id="element-1929">Workspaces</p>
            </div>
          </div>
        </div>
        <div className="bg-white border border-gray-200 rounded-lg p-4" data-id="element-1930">
          <div className="flex items-center gap-3" data-id="element-1931">
            <div className="p-2 bg-purple-50 rounded-lg" data-id="element-1932">
              <Shield className="h-5 w-5 text-purple-600" data-id="element-1933" />
            </div>
            <div data-id="element-1934">
              <p className="text-2xl font-semibold text-gray-900" data-id="element-1935">
                {roles.length}
              </p>
              <p className="text-xs text-gray-500" data-id="element-1936">Roles</p>
            </div>
          </div>
        </div>
        <div className="bg-white border border-gray-200 rounded-lg p-4" data-id="element-1937">
          <div className="flex items-center gap-3" data-id="element-1938">
            <div className="p-2 bg-yellow-50 rounded-lg" data-id="element-1939">
              <UsersIcon className="h-5 w-5 text-yellow-600" data-id="element-1940" />
            </div>
            <div data-id="element-1941">
              <p className="text-2xl font-semibold text-gray-900" data-id="element-1942">
                {allUsers.filter(u => u.status === 'active').length}
              </p>
              <p className="text-xs text-gray-500" data-id="element-1943">Active Users</p>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200" data-id="element-1944">
        <nav className="-mb-px flex space-x-8" data-id="element-1945">
          <button onClick={() => setActiveTab('users')} className={`${activeTab === 'users' ? 'border-blue-500 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'} whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm flex items-center gap-2`} data-id="element-1946">
            <UsersIcon className="h-4 w-4" data-id="element-1947" />
            All Users ({allUsers.length})
          </button>
          <button onClick={() => setActiveTab('workspaces')} className={`${activeTab === 'workspaces' ? 'border-blue-500 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'} whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm flex items-center gap-2`} data-id="element-1948">
            <Building2 className="h-4 w-4" data-id="element-1949" />
            Workspaces ({workspaces.length})
          </button>
        </nav>
      </div>

      {activeTab === 'users' && <div className="space-y-4" data-id="element-1950">
          <div className="flex justify-between items-center" data-id="element-1951">
            <div className="flex items-center gap-2" data-id="element-1952">
              <span className="text-sm text-gray-500" data-id="element-1953">
                Filter by Workspace:
              </span>
              <select className="rounded-md border-gray-300 text-sm focus:ring-blue-500 focus:border-blue-500" value={selectedWorkspaceFilter} onChange={e => setSelectedWorkspaceFilter(e.target.value)} data-id="element-1954">
                <option value="all" data-id="element-1955">
                  All Workspaces ({allUsers.length} users)
                </option>
                {workspaces.map(ws => <option key={ws.id} value={ws.id} data-id="element-1956">
                    {ws.name} (
                    {allUsers.filter(u => u.workspaceId === ws.id).length}{' '}
                    users)
                  </option>)}
              </select>
            </div>
            <button onClick={() => setShowUserModal(true)} className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700" data-id="element-1957">
              <Plus className="h-4 w-4" data-id="element-1958" />
              Create User
            </button>
          </div>

          <div className="bg-white border border-gray-200 rounded-lg overflow-hidden" data-id="element-1959">
            <table className="min-w-full divide-y divide-gray-200" data-id="element-1960">
              <thead className="bg-gray-50" data-id="element-1961">
                <tr data-id="element-1962">
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1963">
                    User
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1964">
                    Workspace
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1965">
                    Role
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1966">
                    Status
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1967">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200" data-id="element-1968">
                {filteredUsers.map(user => <tr key={user.id} className="hover:bg-gray-50" data-id="element-1969">
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-1970">
                      <div className="flex items-center" data-id="element-1971">
                        <div className="h-8 w-8 rounded-full bg-gray-200 flex items-center justify-center text-xs font-medium text-gray-600 mr-3" data-id="element-1972">
                          {user.name.charAt(0)}
                        </div>
                        <div data-id="element-1973">
                          <div className="text-sm font-medium text-gray-900" data-id="element-1974">
                            {user.name}
                          </div>
                          <div className="text-sm text-gray-500" data-id="element-1975">
                            {user.email}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-1976">
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-800" data-id="element-1977">
                        {workspaces.find(w => w.id === user.workspaceId)?.name || user.workspaceId}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-1978">
                      {editingUserId === user.id ? <div className="flex items-center gap-2" data-id="element-1979">
                          <select className="rounded-md border-gray-300 text-sm focus:ring-blue-500 focus:border-blue-500" value={selectedRoleId} onChange={e => setSelectedRoleId(parseInt(e.target.value))} data-id="element-1980">
                            {roles.map(role => <option key={role.id} value={role.id} data-id="element-1981">
                                {role.name}
                              </option>)}
                          </select>
                          <button onClick={() => handleSaveRole(user.id)} className="p-1 text-green-600 hover:text-green-800" data-id="element-1982">
                            <CheckCircle className="h-4 w-4" data-id="element-1983" />
                          </button>
                          <button onClick={() => setEditingUserId(null)} className="p-1 text-gray-400 hover:text-gray-600" data-id="element-1984">
                            <X className="h-4 w-4" data-id="element-1985" />
                          </button>
                        </div> : <div className="flex items-center gap-2" data-id="element-1986">
                          <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${getRoleBadgeColor(user.role)}`} data-id="element-1987">
                            {user.role}
                          </span>
                          {user.id !== currentUser.id && <button onClick={() => handleStartEditRole(user.id, user.roleId)} className="p-1 text-gray-400 hover:text-blue-600" title="Change Role" data-id="element-1988">
                              <Edit2 className="h-3 w-3" data-id="element-1989" />
                            </button>}
                        </div>}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-1990">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${user.status === 'active' ? 'bg-green-100 text-green-800' : user.status === 'suspended' ? 'bg-red-100 text-red-800' : 'bg-yellow-100 text-yellow-800'}`} data-id="element-1991">
                        {user.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium" data-id="element-1992">
                      {user.id !== currentUser.id && <div className="flex items-center justify-end gap-2" data-id="element-1993">
                          {user.status === 'active' ? <button onClick={() => updateUserStatus(user.id, 'suspended')} className="text-yellow-600 hover:text-yellow-900" title="Suspend User" data-id="element-1994">
                              <Ban className="h-4 w-4" data-id="element-1995" />
                            </button> : <button onClick={() => updateUserStatus(user.id, 'active')} className="text-green-600 hover:text-green-900" title="Activate User" data-id="element-1996">
                              <CheckCircle className="h-4 w-4" data-id="element-1997" />
                            </button>}
                          <button onClick={() => deleteUser(user.id)} className="text-red-600 hover:text-red-900" title="Delete User" data-id="element-1998">
                            <Trash2 className="h-4 w-4" data-id="element-1999" />
                          </button>
                        </div>}
                    </td>
                  </tr>)}
              </tbody>
            </table>
          </div>
        </div>}

      {activeTab === 'workspaces' && <div className="space-y-4" data-id="element-2000">
          <div className="flex justify-end" data-id="element-2001">
            <button onClick={() => setShowWorkspaceModal(true)} className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700" data-id="element-2002">
              <Plus className="h-4 w-4" data-id="element-2003" />
              Create Workspace
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-2004">
            {workspaces.map(ws => <div key={ws.id} className="bg-white border border-gray-200 rounded-lg p-6 hover:shadow-sm transition-shadow" data-id="element-2005">
                <div className="flex items-start justify-between mb-4" data-id="element-2006">
                  <div className="p-2 bg-blue-50 rounded-lg" data-id="element-2007">
                    <Building2 className="h-6 w-6 text-blue-600" data-id="element-2008" />
                  </div>
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800" data-id="element-2009">
                    {ws.region}
                  </span>
                </div>
                <h3 className="text-lg font-medium text-gray-900 mb-1" data-id="element-2010">
                  {ws.name}
                </h3>
                <div className="mt-4 space-y-2" data-id="element-2011">
                  <div className="flex justify-between text-sm" data-id="element-2012">
                    <span className="text-gray-500" data-id="element-2013">Users</span>
                    <span className="font-medium text-gray-900" data-id="element-2014">
                      {allUsers.filter(u => u.workspaceId === ws.id).length}
                    </span>
                  </div>
                  <div className="flex justify-between text-sm" data-id="element-2015">
                    <span className="text-gray-500" data-id="element-2016">Sub-Regions</span>
                    <span className="font-medium text-gray-900" data-id="element-2017">
                      {ws.subRegions.length}
                    </span>
                  </div>
                </div>
              </div>)}
          </div>
        </div>}

      <CreateUserModal isOpen={showUserModal} onClose={() => setShowUserModal(false)} data-id="element-2018" />
      <CreateWorkspaceModal isOpen={showWorkspaceModal} onClose={() => setShowWorkspaceModal(false)} data-id="element-2019" />
    </div>;
}